import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSData, DSSetup, RequestError, Service } from 'webapp-types';
import { copyObject, utf8_to_b64 } from 'webapp/utils/common';
import { getDateRangeIdFromFormula } from 'webapp/utils/date-range';

import didUpdate from 'webapp/modifiers/did-update';
import type { CompareDisplayData } from 'webapp/components/block/content/common/compares';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface Args {
	chartState: MetricInsightsChartState;
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
}

export default class MetricDetailsTopDatapointsExploreGoalCompareComponent extends Component<Args> {
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('request') declare requestService: Service.Request;

	private paramHash = '';
	@tracked isLoading = true;
	@tracked dataSelect: DataSelect | null = null;

	private dataQueryXHR: JQuery.jqXHR<unknown> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.recreateDataQuery();
	}

	get isDataLoading() {
		return this.isLoading || this.args.forceLoadingState;
	}

	get currentCompareFormula(): DSSetup.Compare | null {
		const primaryDs = this.args.chartState.block?.blockSetup?.firstShownDataSelect;
		return primaryDs?.limitations.compare?.getFormula() ?? null;
	}

	get goalLabel(): string {
		const key = this.currentCompareFormula?.metricSetup?.key;
		if (!key) {
			return 'vs. goal';
		}
		const goal = this.mmGoalsService.getGoalForGoalKeyLong(key);
		return goal?.title ? `vs. ${goal.title}` : 'vs. goal';
	}

	@cached
	get points() {
		const points: DChartPrimaryPoint[] = [];
		this.dataSelect?.outputs?.forEach((output) => {
			const p = this.mmSamplesService.createDChartPointsFromOutput(output);
			points.push(...p);
		});
		return points;
	}

	get primaryPoint(): DChartPrimaryPoint | null {
		return this.points[0] || null;
	}

	get comparePoint() {
		return this.primaryPoint?.comparePoints[0] || null;
	}

	get compareValuePoint(): DChartPrimaryPoint | null {
		const cp = this.comparePoint;
		if (!cp) {
			return null;
		}
		return new DChartPrimaryPoint({ valueStr: cp.valueStr });
	}

	get primaryComparePoints(): CompareDisplayData[] | undefined {
		return this.primaryPoint?.comparePoints.map((cp) => ({
			...cp,
			indicatorClass: cp.indicatorClass,
			compareText: cp.granulationLabel.labelLong
				? `vs ${cp.granulationLabel.labelLong}`
				: cp.compareText,
		}));
	}

	private setData(data: { previewData: DSData.RootTop | null; paramHash: string }) {
		if (data.paramHash === this.paramHash) {
			this.dataSelect?.setFakeData(data.previewData);
			this.isLoading = false;
		}
	}

	private requestDataQuery(dataSelect: DataSelect, paramHash: string) {
		if (this.dataQueryXHR && this.dataQueryXHR.readyState !== 4) {
			this.dataQueryXHR.abort();
		}

		const promise = this.mmDataSelectsService.fetchData(dataSelect, true);

		promise
			.then((previewData: DSData.RootTop) => {
				this.setData({ previewData, paramHash });
			})
			.catch((errorData: RequestError) => {
				this.setData({ previewData: null, paramHash });
				this.requestService.handleGenericThrownError(errorData);
			});

		this.dataQueryXHR = (promise.jqPromise as JQuery.jqXHR) ?? null;
	}

	private recreateDataQuery() {
		next(() => {
			next(() => {
				// skip while limitations load — primaryDs isn't updated yet
				// re-fires  when forceLoadingState clears
				if (this.args.forceLoadingState) {
					return;
				}
				const primaryDs = this.args.chartState.block?.blockSetup?.firstShownDataSelect;
				const dataSelectData = primaryDs?.toJSON(true);
				const compareFormula = primaryDs?.limitations.compare?.getFormula();
				const dateRangeFormula = this.args.dateRange?.formula;

				if (dataSelectData && compareFormula && dateRangeFormula) {
					const rangeOnlyFormula: DateRangeInterface.FormulaInterface =
						copyObject(dateRangeFormula);
					delete rangeOnlyFormula.granulation;

					const dateRangeId = getDateRangeIdFromFormula(rangeOnlyFormula);
					const newParamHash = utf8_to_b64(
						[
							JSON.stringify(this.args.metricSetup.metricSetupData),
							dateRangeId,
							JSON.stringify(compareFormula),
						].join(''),
					);

					if (this.paramHash !== newParamHash) {
						this.isLoading = true;
						this.dataSelect?.setFakeData(null);
						this.paramHash = newParamHash;

						dataSelectData.id = this.metricDetailsService.generateMetricInsightsUUID();
						dataSelectData.blockId = null;
						const granulation = dataSelectData.dateRange!.granulation!; // a data select always has a granulation
						granulation.isWholeRange = true;
						granulation.timeUnit = null;
						granulation.multiplier = null;
						delete dataSelectData.compares;

						next(() => {
							const dataSelectModel =
								this.mmDataSelectsService.createNewModel(dataSelectData);
							const dateRange = dataSelectModel.limitations.dateRange;

							if (dateRange && !dateRange.isAllTime && !dateRange.isSince) {
								const newCompare = DsCompare.createFromFormula(compareFormula);
								dataSelectModel.changeCompare(
									dataSelectModel.limitations.compareCMs[0]!,
									newCompare,
								);
							}

							this.requestDataQuery(dataSelectModel, newParamHash);
							this.unloadDataSelect();
							this.dataSelect = dataSelectModel;
						});
					}
				} else {
					this.paramHash = '';
				}
			});
		});
	}

	@action
	refreshDataQuery() {
		once(this, this.recreateDataQuery);
	}

	private unloadDataSelect() {
		if (this.dataSelect) {
			this.dataSelect.model.unloadRecord();
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.dataQueryXHR?.abort();
		this.unloadDataSelect();
	}

	<template>
		<div
			{{didUpdate
				this.refreshDataQuery
				@metricSetup.key
				@metricSetup.dimensions
				@metricSetup.filtersData
				@dateRange.formulaId
				@forceLoadingState
				this.currentCompareFormula
			}}
		>
			<MetricDetailsTopDatapointsDatapointCardComponent
				@label={{this.goalLabel}}
				@valuePoint={{this.compareValuePoint}}
				@comparePoints={{this.primaryComparePoints}}
				@skipCompareValue={{true}}
				@isLoading={{this.isDataLoading}}
			/>
		</div>
	</template>
}
